import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import api from 'api';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { PHONE_NUMBER_MUTATION_KEY } from 'hooks/usePhoneNumber';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import {
  INTEGRATION_LIST,
  type Integration,
  type IntegrationCategory,
  type TelephonyIntegration,
} from '../constants';
import type { IntegrationsResponse } from '../types';
import useIntegrationRecords from './useIntegrationRecords';

type ActiveIntegrations = Record<IntegrationCategory, IntegrationsResponse[]>;

export const useIntegrations = () => {
  const { accountId, projectId } = useProjectRoute();
  const { hasProjectResourceWriteAccess } = useProjectResource();
  const queryClient = useQueryClient();
  const isPlgProd = useIsPlgProd();
  const { isFeatureOn } = useIsFeatureOn();
  const isIntegrationConfigurationEnabled = isFeatureOn(
    'integrations_configuration',
  );
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const activeTelephonyIntegrations = useQuery({
    queryKey: [accountId, projectId, 'active-telephony-integrations'],
    queryFn: () => api.getActiveTelephonyIntegrations(accountId, projectId),
    enabled:
      !!accountId &&
      !!projectId &&
      !isPlgProd &&
      isIntegrationConfigurationEnabled,
  });

  const {
    data: activeIntegrationRecords,
    isLoading: isLoadingIntegrationRecords,
  } = useIntegrationRecords();

  const activeIntegrations: ActiveIntegrations = useMemo(() => {
    return (activeIntegrationRecords ?? [])?.reduce(
      (acc, integrationRecord) => {
        const provider = integrationRecord.integration;
        const category = INTEGRATION_LIST.find(
          integration => integration.id === provider,
        )?.category;

        if (!category) {
          return acc;
        }

        acc[category].push({
          id: parseInt(integrationRecord.id),
          provider_name: provider,
          account_id: integrationRecord.account_id,
          project_id: integrationRecord.project_id,
          config: integrationRecord.data,
        });
        return acc;
      },
      {
        telephony: activeTelephonyIntegrations.data ?? [],
        chat: [],
        crm: [],
        hospitality: [],
        healthcare: [],
        knowledgeBase: [],
        projectManagement: [],
      } as ActiveIntegrations,
    );
  }, [activeTelephonyIntegrations.data, activeIntegrationRecords]);

  const disconnectIntegration = async (disconnectEndpoint: string) => {
    if (!accountId || !projectId) {
      throw new Error('Account ID and Project ID are required');
    }

    setIsDisconnecting(true);
    try {
      await api.disconnectTelephonyIntegration({
        accountId,
        projectId,
        disconnectEndpoint,
      });

      // Invalidate and refetch active integrations
      await queryClient.invalidateQueries({
        queryKey: [accountId, projectId, 'active-telephony-integrations'],
      });

      // Invalidate phone numbers
      await queryClient.invalidateQueries({
        queryKey: [accountId, projectId, PHONE_NUMBER_MUTATION_KEY],
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const canConnectIntegration = (integration: Integration) => {
    if (integration.category === 'telephony') {
      // Some telephony integrations only support one handoff method, so we need to reset the handoff configs when connecting them to ensure we don't have any invalid configs.
      // Since this edits handoff configurations, we need to check if the user has write access to the handoffs resource before doing this.
      if (integration.additionalConfig?.shouldResetHandoffConfigs) {
        return (
          hasProjectResourceWriteAccess(PROJECT_RESOURCE.HANDOFFS) &&
          activeIntegrations.telephony.length === 0
        );
      }
      return activeIntegrations.telephony.length === 0;
    }
    return true;
  };

  const activeTelephonyIntegration: TelephonyIntegration | null =
    useMemo(() => {
      const found = INTEGRATION_LIST.find(
        integration =>
          integration.category === 'telephony' &&
          integration.id === activeIntegrations.telephony[0]?.provider_name,
      );
      return (
        found?.category === 'telephony' ? found : null
      ) as TelephonyIntegration | null;
    }, [activeIntegrations.telephony]);

  return {
    isLoading: isLoadingIntegrationRecords,
    activeIntegrations,
    activeTelephonyIntegration,
    disconnectIntegration,
    isDisconnecting,
    canConnectIntegration,
    activeIntegrationRecords,
  };
};
